// src/components/Leadership/LeadershipHero.js
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, TrendingUp, Users } from 'lucide-react';
import '../../styles/Leadership/LeadershipHero.css';

const LeadershipHero = () => {
 // Load Calendly widget script once if not present
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // If Calendly is already available, nothing to do
    if (window.Calendly) return;

    // Avoid adding script multiple times
    if (!document.querySelector('script[data-calendly-widget]')) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      script.setAttribute('data-calendly-widget', 'true');
      document.body.appendChild(script);
    }
  }, []);

  const handleBookConsult = () => {
    const calendlyUrl = 'https://calendly.com/edsytech-info/30min';

    // Preferred: open Calendly popup if widget loaded
    if (window?.Calendly?.showPopupWidget) {
      try {
        window.Calendly.showPopupWidget(calendlyUrl);
      } catch (e) {
        // fallback to opening new tab
        window.open(calendlyUrl, '_blank', 'noopener');
      }
    } else {
      // If widget not available yet, open calendly in a new tab
      window.open(calendlyUrl, '_blank', 'noopener');
    }
  };

  return (
    <section className="leadership-hero">
      <div className="leadership-hero-container">
        <div className="leadership-hero-content">
          {/* Badge */}
          <div className="leadership-badge animate-fade-in">
            <Shield className="badge-icon" />
            <span>Leadership, Governance & AI-Driven Platforms</span>
          </div>

          {/* Main Headline */}
          <h1 className="leadership-hero-headline">
            Technology you can trust.
            <br />
            Leadership you can measure.
            <br />
            <span className="headline-gradient">AI that powers real businesses.</span>
          </h1>

          {/* Subheadline */}
          <p className="leadership-hero-subheadline">
            Edsy Tech empowers organizations with SaaS platforms backed by accountability,
            KPIs, and board‑level confidence — plus AI-driven products like Snippy and Sesslink
            that put those same principles to work for real businesses today.
          </p>

          {/* Trust Indicators */}
          <div className="trust-indicators">
            <div className="trust-indicator">
              <div className="trust-indicator-icon">
                <TrendingUp className="icon" />
              </div>
              <div className="trust-indicator-content">
                <div className="trust-indicator-value">Measurable</div>
                <div className="trust-indicator-label">KPIs & Outcomes</div>
              </div>
            </div>
            <div className="trust-indicator">
              <div className="trust-indicator-icon">
                <Shield className="icon" />
              </div>
              <div className="trust-indicator-content">
                <div className="trust-indicator-value">Accountable</div>
                <div className="trust-indicator-label">Clear Ownership</div>
              </div>
            </div>
            <div className="trust-indicator">
              <div className="trust-indicator-icon">
                <Users className="icon" />
              </div>
              <div className="trust-indicator-content">
                <div className="trust-indicator-value">Transparent</div>
                <div className="trust-indicator-label">Board-Ready Reports</div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="leadership-hero-cta">
            <div className="cta-button-row">
              <button className="cta-primary-large" onClick={handleBookConsult}>
                <span className="cta-emoji">👉</span>
                Book a Strategy Call
                <ArrowRight className="cta-arrow" />
              </button>
              <Link to="/products" className="cta-secondary-large">
                Explore Our AI Products
                <ArrowRight className="cta-arrow" />
              </Link>
            </div>
            <p className="cta-subtext">
              15-minute consultation with senior leadership
            </p>
          </div>
        </div>

        {/* Visual Dashboard */}
        <div className="leadership-visual">
          <div className="governance-dashboard">
            <div className="dashboard-header">
              <h3 className="dashboard-title">Executive Dashboard</h3>
              <span className="dashboard-status">Live</span>
            </div>

            <div className="dashboard-metrics">
              <div className="metric-box metric-primary">
                <div className="metric-label">KPI Achievement</div>
                <div className="metric-value">94%</div>
                <div className="metric-trend positive">↑ 12% this quarter</div>
              </div>
              <div className="metric-box metric-secondary">
                <div className="metric-label">SLA Compliance</div>
                <div className="metric-value">99.9%</div>
                <div className="metric-trend positive">On target</div>
              </div>
            </div>

            <div className="dashboard-reports">
              <div className="report-item">
                <div className="report-icon">📊</div>
                <div className="report-content">
                  <div className="report-title">WSR Ready</div>
                  <div className="report-status">Updated 2h ago</div>
                </div>
              </div>
              <div className="report-item">
                <div className="report-icon">📅</div>
                <div className="report-content">
                  <div className="report-title">MSR Available</div>
                  <div className="report-status">Q4 2024</div>
                </div>
              </div>
              <div className="report-item">
                <div className="report-icon">⚡</div>
                <div className="report-content">
                  <div className="report-title">Zero Incidents</div>
                  <div className="report-status">30 days</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadershipHero;